import React, { useState } from 'react';
import { 
  Tv, 
  Plus, 
  Trash2, 
  RefreshCw, 
  CheckCircle2, 
  AlertCircle, 
  ExternalLink, 
  Radio, 
  Film, 
  Layers, 
  Server, 
  ShieldCheck, 
  Sliders, 
  Globe, 
  Key, 
  User, 
  Clock, 
  Database,
  Edit2,
  Check,
  X
} from 'lucide-react';
import { IptvSource, IptvSyncProgress } from '../../types';
import { testIptvConnection, syncIptvSource, clearCachedSource } from '../../services/iptvService';
import { saveIptvSource, deleteIptvSource } from '../../lib/firebase';

interface IptvManagerTabProps {
  sources: IptvSource[];
  userId?: string;
  onRefreshNeeded?: () => void;
}

export const IptvManagerTab: React.FC<IptvManagerTabProps> = ({
  sources,
  userId,
  onRefreshNeeded
}) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingSource, setEditingSource] = useState<IptvSource | null>(null);

  // Form State
  const [sourceType, setSourceType] = useState<'m3u' | 'xtream'>('m3u');
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [serverUrl, setServerUrl] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [userAgent, setUserAgent] = useState('IPTVSmartersPro');
  const [enabled, setEnabled] = useState(true);

  // Testing & Sync State
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string; latency?: number; userInfo?: any; serverInfo?: any } | null>(null);
  const [syncingId, setSyncingId] = useState<string | null>(null);
  const [syncProgress, setSyncProgress] = useState<IptvSyncProgress | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);

  const resetForm = () => {
    setSourceType('m3u');
    setName('');
    setUrl('');
    setServerUrl('');
    setUsername('');
    setPassword('');
    setUserAgent('IPTVSmartersPro');
    setEnabled(true);
    setTestResult(null);
    setEditingSource(null);
    setShowAddModal(false);
  };

  const handleOpenEdit = (s: IptvSource) => {
    setEditingSource(s);
    setSourceType(s.type);
    setName(s.name);
    setUrl(s.url || '');
    setServerUrl(s.serverUrl || '');
    setUsername(s.username || '');
    setPassword(s.password || '');
    setUserAgent(s.userAgent || 'IPTVSmartersPro');
    setEnabled(s.enabled ?? true);
    setTestResult(null);
    setShowAddModal(true);
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await testIptvConnection({
        type: sourceType,
        url,
        serverUrl,
        username,
        password,
        userAgent: (userAgent || 'IPTVSmartersPro').trim()
      });
      setTestResult(res);
    } catch (err: any) {
      setTestResult({
        success: false,
        message: err.message || 'Error en connectar.'
      });
    } finally {
      setTesting(false);
    }
  };

  const handleSaveSource = async (e: React.FormEvent) => {
    e.preventDefault();
    setActionError(null);

    if (!name.trim()) {
      setActionError('Cal assignar un nom a la font IPTV.');
      return;
    }

    if (sourceType === 'm3u' && !url.trim()) {
      setActionError('Cal introduir la URL de la llista M3U / M3U8.');
      return;
    }

    if (sourceType === 'xtream' && (!serverUrl.trim() || !username.trim() || !password.trim())) {
      setActionError('Cal introduir el servidor Xtream, el nom d\'usuari i la contrasenya.');
      return;
    }

    try {
      const newSource: IptvSource = {
        id: editingSource ? editingSource.id : `iptv_${Date.now()}`,
        name: name.trim(),
        type: sourceType,
        url: sourceType === 'm3u' ? url.trim() : undefined,
        serverUrl: sourceType === 'xtream' ? serverUrl.trim() : undefined,
        username: sourceType === 'xtream' ? username.trim() : undefined,
        password: sourceType === 'xtream' ? password.trim() : undefined,
        userAgent: (userAgent || 'IPTVSmart').trim(),
        enabled,
        createdAt: editingSource?.createdAt || new Date().toISOString(),
        status: editingSource?.status || 'idle',
        liveCount: editingSource?.liveCount,
        moviesCount: editingSource?.moviesCount,
        seriesCount: editingSource?.seriesCount
      };

      await saveIptvSource(newSource, userId);
      setActionSuccess(`Font "${newSource.name}" desada correctament.`);
      resetForm();
      onRefreshNeeded?.();

      // Automatically trigger sync for new source
      if (!editingSource) {
        handleSyncSource(newSource);
      }
    } catch (err: any) {
      setActionError(err.message || 'Error en desar la font IPTV.');
    }
  };

  const handleDelete = async (sourceId: string, sourceName: string) => {
    if (!confirm(`Segur que vols eliminar la font IPTV "${sourceName}"? S'esborraran tots els canals i contingut en memòria cau associat.`)) {
      return;
    }

    try {
      await deleteIptvSource(sourceId, userId);
      await clearCachedSource(sourceId);
      setActionSuccess(`Font "${sourceName}" eliminada.`);
      onRefreshNeeded?.();
    } catch (err: any) {
      setActionError(err.message || 'Error en eliminar la font IPTV.');
    }
  };

  const handleSyncSource = async (source: IptvSource) => {
    setSyncingId(source.id);
    setActionError(null);
    setSyncProgress(null);

    try {
      const result = await syncIptvSource(source, (progress) => {
        setSyncProgress(progress);
      });

      // Update source metadata
      const updatedSource: IptvSource = {
        ...source,
        lastSyncedAt: new Date().toISOString(),
        liveCount: result.channels.length,
        moviesCount: result.movies.length,
        seriesCount: result.series.length,
        status: 'active',
        lastError: undefined
      };

      await saveIptvSource(updatedSource, userId);
      setActionSuccess(`Sincronitzat amb èxit: ${result.channels.length} canals, ${result.movies.length} pel·lícules, ${result.series.length} sèries.`);
      onRefreshNeeded?.();
    } catch (err: any) {
      console.error('Sync IPTV Error:', err);
      setActionError(`Error en sincronitzar ${source.name}: ${err.message}`);
      
      const errorSource: IptvSource = {
        ...source,
        status: 'error',
        lastError: err.message
      };
      await saveIptvSource(errorSource, userId).catch(() => {});
    } finally {
      setSyncingId(null);
      setTimeout(() => setSyncProgress(null), 4000);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header & Controls */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-slate-900/60 p-5 rounded-2xl border border-slate-800">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2.5">
            <div className="p-2 rounded-xl bg-red-600/20 text-red-500 border border-red-500/30">
              <Tv className="w-5 h-5" />
            </div>
            Gestió de Fonts IPTV (M3U & Xtream Codes)
          </h2>
          <p className="text-sm text-slate-400 mt-1">
            Afegeix i sincronitza llistes M3U o servidors Xtream Codes amb reproducció proxy lliure de CORS i categories detallades.
          </p>
        </div>

        <button
          id="btn-add-iptv-source"
          onClick={() => {
            resetForm();
            setShowAddModal(true);
          }}
          className="flex items-center gap-2 px-4 py-2.5 bg-red-600 hover:bg-red-700 active:scale-95 text-white font-medium rounded-xl transition-all shadow-lg shadow-red-600/20"
        >
          <Plus className="w-4 h-4" />
          Afegir Font IPTV
        </button>
      </div>

      {/* Alert Banners */}
      {actionSuccess && (
        <div className="p-4 rounded-xl bg-emerald-950/40 border border-emerald-500/30 text-emerald-300 flex items-center justify-between text-sm animate-in fade-in">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
            <span>{actionSuccess}</span>
          </div>
          <button onClick={() => setActionSuccess(null)} className="text-emerald-400 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {actionError && (
        <div className="p-4 rounded-xl bg-red-950/40 border border-red-500/30 text-red-300 flex items-center justify-between text-sm animate-in fade-in">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-red-400 shrink-0" />
            <span>{actionError}</span>
          </div>
          <button onClick={() => setActionError(null)} className="text-red-400 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Sync Progress Bar */}
      {syncProgress && (
        <div className="p-4 rounded-2xl bg-slate-900 border border-red-500/30 space-y-2 shadow-xl animate-in slide-in-from-top">
          <div className="flex justify-between text-xs font-medium text-slate-300">
            <span className="flex items-center gap-2 text-red-400">
              <RefreshCw className="w-3.5 h-3.5 animate-spin" />
              {syncProgress.message}
            </span>
            <span>{syncProgress.progressPercent}%</span>
          </div>
          <div className="w-full bg-slate-800 rounded-full h-2 overflow-hidden">
            <div 
              className="bg-gradient-to-r from-red-600 to-amber-500 h-full transition-all duration-300"
              style={{ width: `${syncProgress.progressPercent}%` }}
            />
          </div>
        </div>
      )}

      {/* IPTV Sources Grid / List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {sources.map((source) => {
          const isSyncing = syncingId === source.id;
          return (
            <div
              key={source.id}
              className={`p-5 rounded-2xl border transition-all relative flex flex-col justify-between ${
                source.enabled 
                  ? 'bg-slate-900/80 border-slate-800 hover:border-slate-700 shadow-lg' 
                  : 'bg-slate-950/40 border-slate-900 opacity-60'
              }`}
            >
              <div>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-2.5">
                    <div className={`p-2.5 rounded-xl border ${
                      source.type === 'xtream' 
                        ? 'bg-amber-500/10 text-amber-400 border-amber-500/30' 
                        : 'bg-red-500/10 text-red-400 border-red-500/30'
                    }`}>
                      {source.type === 'xtream' ? <Server className="w-5 h-5" /> : <Tv className="w-5 h-5" />}
                    </div>
                    <div>
                      <h3 className="font-semibold text-white text-base leading-tight">{source.name}</h3>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`text-[11px] font-bold px-2 py-0.5 rounded-md uppercase tracking-wider ${
                          source.type === 'xtream'
                            ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30'
                            : 'bg-red-500/20 text-red-300 border border-red-500/30'
                        }`}>
                          {source.type === 'xtream' ? 'Xtream Codes' : 'M3U / M3U8'}
                        </span>
                        {source.userAgent && (
                          <span className="text-[10px] text-slate-400 font-mono bg-slate-800 px-1.5 py-0.5 rounded">
                            UA: {source.userAgent}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleOpenEdit(source)}
                      className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
                      title="Editar font"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(source.id, source.name)}
                      className="p-1.5 rounded-lg text-slate-400 hover:text-red-400 hover:bg-red-950/40 transition-colors"
                      title="Eliminar font"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Source details */}
                <div className="mt-4 pt-3 border-t border-slate-800/80 space-y-2 text-xs text-slate-400">
                  {source.type === 'm3u' ? (
                    <div className="truncate flex items-center gap-1.5" title={source.url}>
                      <Globe className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                      <span className="truncate">{source.url}</span>
                    </div>
                  ) : (
                    <div className="space-y-1">
                      <div className="truncate flex items-center gap-1.5" title={source.serverUrl}>
                        <Server className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                        <span className="truncate">{source.serverUrl}</span>
                      </div>
                      <div className="flex items-center gap-1.5 text-slate-400">
                        <User className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                        <span>Usuari: <strong className="text-slate-300 font-mono">{source.username}</strong></span>
                      </div>
                    </div>
                  )}

                  {/* Channel & VOD counts */}
                  <div className="grid grid-cols-3 gap-2 py-2 text-center bg-slate-950/60 rounded-xl border border-slate-800/60 mt-3">
                    <div>
                      <div className="text-[10px] text-slate-400 font-medium">TV Directe</div>
                      <div className="text-sm font-bold text-red-400">{source.liveCount ?? 0}</div>
                    </div>
                    <div>
                      <div className="text-[10px] text-slate-400 font-medium">Pel·lícules</div>
                      <div className="text-sm font-bold text-amber-400">{source.moviesCount ?? 0}</div>
                    </div>
                    <div>
                      <div className="text-[10px] text-slate-400 font-medium">Sèries</div>
                      <div className="text-sm font-bold text-emerald-400">{source.seriesCount ?? 0}</div>
                    </div>
                  </div>

                  {source.lastSyncedAt && (
                    <div className="flex items-center gap-1.5 text-[11px] text-slate-400">
                      <Clock className="w-3 h-3 text-slate-500" />
                      <span>Darrer sincronitzat: {new Date(source.lastSyncedAt).toLocaleString('ca-ES', { dateStyle: 'short', timeStyle: 'short' })}</span>
                    </div>
                  )}

                  {source.lastError && (
                    <div className="text-[11px] text-red-400 bg-red-950/30 p-2 rounded-lg border border-red-500/20">
                      {source.lastError}
                    </div>
                  )}
                </div>
              </div>

              {/* Action Buttons Inside Card */}
              <div className="mt-4 pt-3 border-t border-slate-800/80 flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-1.5">
                  <button
                    id={`btn-edit-iptv-${source.id}`}
                    onClick={() => handleOpenEdit(source)}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 bg-slate-800 hover:bg-slate-700 active:scale-95 text-slate-200 text-xs font-semibold rounded-lg transition-all border border-slate-700 shadow-sm"
                    title="Editar paràmetres de la llista IPTV"
                  >
                    <Edit2 className="w-3.5 h-3.5 text-blue-400" />
                    Editar
                  </button>
                  <button
                    id={`btn-delete-iptv-${source.id}`}
                    onClick={() => handleDelete(source.id, source.name)}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 bg-red-950/30 hover:bg-red-900/50 active:scale-95 text-red-300 text-xs font-semibold rounded-lg transition-all border border-red-500/30 shadow-sm"
                    title="Eliminar aquesta llista IPTV"
                  >
                    <Trash2 className="w-3.5 h-3.5 text-red-400" />
                    Eliminar
                  </button>
                </div>

                <button
                  id={`btn-sync-iptv-${source.id}`}
                  onClick={() => handleSyncSource(source)}
                  disabled={isSyncing}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600/90 hover:bg-red-600 active:scale-95 text-white text-xs font-bold rounded-lg transition-all shadow-md shadow-red-600/20 disabled:opacity-50 ml-auto"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${isSyncing ? 'animate-spin' : ''}`} />
                  {isSyncing ? 'Sincronitzant...' : 'Sincronitzar'}
                </button>
              </div>
            </div>
          );
        })}

        {sources.length === 0 && (
          <div className="col-span-full py-12 text-center bg-slate-900/40 rounded-2xl border border-slate-800 text-slate-400 space-y-3">
            <Tv className="w-10 h-10 mx-auto text-slate-600" />
            <p className="text-base font-medium text-slate-300">No hi ha cap font IPTV configurada.</p>
            <p className="text-xs text-slate-400 max-w-md mx-auto">
              Afegeix una llista de reproducció M3U (com canals TDT lliures) o un compte Xtream Codes per veure canals en directe, pel·lícules i sèries.
            </p>
          </div>
        )}
      </div>

      {/* Add / Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in">
          <div className="bg-slate-900 border border-slate-700/80 rounded-2xl max-w-lg w-full p-6 shadow-2xl relative max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-5 pb-3 border-b border-slate-800">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Tv className="w-5 h-5 text-red-500" />
                {editingSource ? 'Editar Font IPTV' : 'Afegir Nova Font IPTV'}
              </h3>
              <button
                onClick={resetForm}
                className="text-slate-400 hover:text-white p-1 rounded-lg hover:bg-slate-800 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSaveSource} className="space-y-4">
              {/* Type Selector */}
              <div>
                <label className="block text-xs font-medium text-slate-300 mb-1.5">Tipus de Font</label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setSourceType('m3u')}
                    className={`py-2.5 px-4 rounded-xl border text-xs font-semibold flex items-center justify-center gap-2 transition-all ${
                      sourceType === 'm3u'
                        ? 'bg-red-600 text-white border-red-500 shadow-md shadow-red-600/20'
                        : 'bg-slate-800 text-slate-300 border-slate-700 hover:bg-slate-700'
                    }`}
                  >
                    <Tv className="w-4 h-4" />
                    Llista M3U / M3U8
                  </button>

                  <button
                    type="button"
                    onClick={() => setSourceType('xtream')}
                    className={`py-2.5 px-4 rounded-xl border text-xs font-semibold flex items-center justify-center gap-2 transition-all ${
                      sourceType === 'xtream'
                        ? 'bg-amber-600 text-white border-amber-500 shadow-md shadow-amber-600/20'
                        : 'bg-slate-800 text-slate-300 border-slate-700 hover:bg-slate-700'
                    }`}
                  >
                    <Server className="w-4 h-4" />
                    Xtream Codes API
                  </button>
                </div>
              </div>

              {/* Source Name */}
              <div>
                <label className="block text-xs font-medium text-slate-300 mb-1">Nom Identificador</label>
                <input
                  id="iptv-source-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Ex: TDT Catalunya, Canals Premium..."
                  className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-white text-sm focus:outline-none focus:border-red-500 transition-colors"
                  required
                />
              </div>

              {/* Fields for M3U */}
              {sourceType === 'm3u' ? (
                <div>
                  <label className="block text-xs font-medium text-slate-300 mb-1">URL de la Llista M3U / M3U8</label>
                  <input
                    id="iptv-m3u-url"
                    type="url"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    placeholder="https://servidor.com/playlist.m3u"
                    className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-white text-sm focus:outline-none focus:border-red-500 transition-colors"
                    required
                  />
                  <p className="text-[11px] text-slate-400 mt-1">
                    Accepta enllaços HTTP/HTTPS a arxius .m3u o .m3u8 remots. Es processaran per evitar bloquejos CORS.
                  </p>
                </div>
              ) : (
                /* Fields for Xtream Codes */
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-300 mb-1">URL del Servidor (amb Port)</label>
                    <input
                      id="iptv-xtream-server"
                      type="text"
                      value={serverUrl}
                      onChange={(e) => setServerUrl(e.target.value)}
                      placeholder="http://iptv.example.com:8080"
                      className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-white text-sm focus:outline-none focus:border-amber-500 transition-colors"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-300 mb-1">Usuari</label>
                      <input
                        id="iptv-xtream-user"
                        type="text"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        placeholder="usuari123"
                        className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-white text-sm focus:outline-none focus:border-amber-500 transition-colors"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-300 mb-1">Contrasenya</label>
                      <input
                        id="iptv-xtream-pass"
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="••••••••"
                        className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-white text-sm focus:outline-none focus:border-amber-500 transition-colors"
                        required
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Advanced Settings (User-Agent) */}
              <div className="pt-2 border-t border-slate-800">
                <label className="block text-xs font-medium text-slate-300 mb-1 flex items-center justify-between">
                  <span>User-Agent Personalitzat</span>
                  <span className="text-[10px] text-red-400 font-mono">Per defecte: IPTVSmartersPro</span>
                </label>
                <input
                  id="iptv-user-agent"
                  type="text"
                  value={userAgent}
                  onChange={(e) => setUserAgent(e.target.value)}
                  placeholder="IPTVSmartersPro"
                  className="w-full px-3.5 py-2 bg-slate-950 border border-slate-800 rounded-xl text-slate-300 text-xs font-mono focus:outline-none focus:border-slate-600 transition-colors"
                />
                <p className="text-[11px] text-slate-400 mt-1">
                  Requerit per la majoria de proveïdors IPTV per autoritzar la transmissió. S&apos;utilitza &quot;IPTVSmartersPro&quot; per defecte per màxima compatibilitat.
                </p>
              </div>

              {/* Connection Test feedback & Xtream Diagnostics */}
              {testResult && (
                <div className={`p-3.5 rounded-xl border text-xs space-y-2 ${
                  testResult.success
                    ? 'bg-emerald-950/40 border-emerald-500/30 text-emerald-300'
                    : 'bg-red-950/40 border-red-500/30 text-red-300'
                }`}>
                  <div className="flex items-start gap-2.5">
                    {testResult.success ? (
                      <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                    ) : (
                      <AlertCircle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
                    )}
                    <div className="space-y-0.5 flex-1">
                      <div className="font-semibold text-white">{testResult.message}</div>
                      {testResult.latency && (
                        <div className="text-[10px] text-slate-400">Temps de resposta: {testResult.latency}ms</div>
                      )}
                    </div>
                  </div>

                  {/* Xtream account diagnostics badge info */}
                  {testResult.userInfo && (
                    <div className="mt-2 pt-2 border-t border-white/10 grid grid-cols-2 sm:grid-cols-3 gap-2 text-[11px] text-slate-300">
                      {testResult.userInfo.status && (
                        <div className="bg-black/30 p-1.5 rounded-lg border border-white/5">
                          <span className="text-slate-500 block text-[9px] uppercase font-bold">Estat</span>
                          <span className={testResult.userInfo.status === 'Active' ? 'text-emerald-400 font-semibold' : 'text-red-400 font-semibold'}>
                            {testResult.userInfo.status}
                          </span>
                        </div>
                      )}
                      {testResult.userInfo.expDateFormatted && (
                        <div className="bg-black/30 p-1.5 rounded-lg border border-white/5">
                          <span className="text-slate-500 block text-[9px] uppercase font-bold">Caducitat</span>
                          <span className="font-semibold text-white">{testResult.userInfo.expDateFormatted}</span>
                        </div>
                      )}
                      {testResult.userInfo.maxConnections !== undefined && (
                        <div className="bg-black/30 p-1.5 rounded-lg border border-white/5">
                          <span className="text-slate-500 block text-[9px] uppercase font-bold">Connexions</span>
                          <span className="font-semibold text-white">
                            {testResult.userInfo.activeConnections ?? 0} / {testResult.userInfo.maxConnections || 1}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="pt-4 border-t border-slate-800 flex items-center justify-between gap-3">
                <button
                  type="button"
                  onClick={handleTestConnection}
                  disabled={testing}
                  className="px-3 py-2 bg-slate-800 hover:bg-slate-700 active:scale-95 text-slate-200 text-xs font-medium rounded-xl transition-all border border-slate-700 flex items-center gap-1.5 disabled:opacity-50"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${testing ? 'animate-spin text-red-400' : ''}`} />
                  {testing ? 'Comprovant...' : 'Provar Connexió'}
                </button>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="px-4 py-2 bg-transparent hover:bg-slate-800 text-slate-400 text-xs font-medium rounded-xl transition-colors"
                  >
                    Cancel·lar
                  </button>
                  <button
                    type="submit"
                    className="px-5 py-2 bg-red-600 hover:bg-red-700 active:scale-95 text-white text-xs font-semibold rounded-xl transition-all shadow-lg shadow-red-600/20"
                  >
                    {editingSource ? 'Desar Canvis' : 'Afegir Font'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
